'use client';

import { useFormTemplateKey } from '@commenda/api/form-builder/form-template-keys';
import { useFormsTemplate } from '@commenda/api/form-builder/forms-templates';
import { Badge, Button, Icons } from '@commenda/kakashi';
import { cn } from '@commenda/utils';
import { useDroppable } from '@dnd-kit/core';
import {
  AnimateLayoutChanges,
  defaultAnimateLayoutChanges,
  horizontalListSortingStrategy,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Clock,
  Copy,
  GripVertical,
  Plus,
  Sparkles,
  Trash2,
} from 'lucide-react';
import React from 'react';
import { isCommendaOSComponentInstance } from './editor/ComponentPalette/components';
import { FormBuilderHook } from './hooks/useFormBuilder';
import { ComponentType, isModuleLockingComponent } from './types';
import type { FormComponent, FormModule, FormStep, Selection } from '@commenda/types';

// Custom animate layout changes - always animate except when being dragged
const animateLayoutChanges: AnimateLayoutChanges = (args) =>
  defaultAnimateLayoutChanges({ ...args, wasDragging: true });

// Component type icons mapping
const componentIcons: Record<
  ComponentType,
  React.FC<{ className?: string }>
> = {
  Alert: Icons.AlertCircle,
  Input: Icons.Edit03,
  TextArea: Icons.AlignLeft,
  RadioGroup: Icons.CheckCircleBroken,
  Select: Icons.ChevronDown,
  // AsyncSelect: Icons.SearchLg,
  // AutoComplete: Icons.SearchLg,
  // NameAvailabilityChecker: Icons.CheckCircle,
  FileUpload: Icons.Upload01,
  MultiFileUpload: Icons.Upload01,
  PhoneInput: Icons.Phone,
  AddressInput: Icons.MarkerPin01,
  PasswordInput: Icons.Lock01,
  NumberInput: Icons.Hash01,
  // DynamicFieldArray: Icons.List,
  CheckboxGroup: Icons.CheckSquare,
  // Button: Icons.CursorClick01,
  // UpsellCard: Icons.CreditCard01,
  FormSummary: Icons.File02,
  NestedForm: Icons.LinkExternal01,
  UrlInput: Icons.Link01,
  DateInput: Icons.Calendar,
  CountryInput: Icons.Globe01,
  // Commenda OS components
  Location: Icons.MarkerPin01,
  RegisteredAgent: Icons.UserSquare,
  EntitySelect: Icons.Database01,
  AutoDetectField: Icons.MagicWand01,
  GovernmentIdentifier: Icons.Hash01,
  BankAccount: Icons.CreditCard01,
  EntitySelectOrCreate: Icons.Database01,
  BusinessEntitySelectOrCreate: Icons.Building07,
  Document: Icons.File02,
  DynamicFields: Icons.List,
  KeyPersons: Icons.Users01,
  DirectorDocument: Icons.File02,
  RoleAssignment: Icons.UserCheck01,
  Ownership: Icons.PieChart01,
  IndirectTaxRegistration: Icons.Receipt,
};

// Resolve linked form title for NestedForm component (key -> template name)
function NestedFormTitle({
  keyId,
  fallback,
}: {
  keyId?: string | null;
  fallback: string;
}) {
  const { key } = useFormTemplateKey(keyId ?? null);
  const { template } = useFormsTemplate(key?.liveFormId ?? null);
  const title = template?.name ?? key?.name ?? fallback;
  return <>{title}</>;
}

// Map resolved field types (from DSL schema) to the component type that renders them
// This ensures AutoDetectField icons match the actual component icons
const resolvedTypeToComponentType: Record<string, ComponentType> = {
  date: 'DateInput',
  url: 'UrlInput',
  fiscalYear: 'DateInput',
  number: 'NumberInput',
  enum: 'Select',
  boolean: 'Select',
  string: 'Input',
};

/**
 * Get the icon component for a form component.
 * For AutoDetectField, derives the icon from the resolved field type.
 */
function getIconForComponent(
  component: FormComponent
): React.FC<{ className?: string }> {
  // Special handling for AutoDetectField - derive icon from resolved type
  if (component.component === 'AutoDetectField') {
    const resolvedType = component.props?.resolvedFieldType as
      | string
      | undefined;
    if (resolvedType) {
      const mappedComponentType = resolvedTypeToComponentType[resolvedType];
      if (mappedComponentType && componentIcons[mappedComponentType]) {
        return componentIcons[mappedComponentType];
      }
    }
    // Default sparkles icon when not configured
    return Sparkles;
  }

  // For EntitySelect, use a building icon
  if (component.component === 'EntitySelect') {
    return Icons.Building07;
  }

  // For BusinessEntitySelectOrCreate, use a building icon
  if (component.component === 'BusinessEntitySelectOrCreate') {
    return Icons.Building07;
  }

  // For EntitySelectOrCreate, use a users icon
  if (component.component === 'EntitySelectOrCreate') {
    return Icons.Users01;
  }

  // For KeyPersons, use a users icon
  if (component.component === 'KeyPersons') {
    return Icons.Users01;
  }

  return componentIcons[component.component] || Icons.Edit03;
}

interface SortableComponentProps {
  component: FormComponent;
  stepId: string;
  moduleId: string;
  isSelected: boolean;
  isNewlyAdded: boolean;
  onSelect: () => void;
  onRemove: () => void;
  onDuplicate: () => void;
}

function SortableComponent({
  component,
  stepId,
  moduleId,
  isSelected,
  isNewlyAdded,
  onSelect,
  onRemove,
  onDuplicate,
}: SortableComponentProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: component.id,
    animateLayoutChanges,
    data: {
      type: 'component',
      stepId,
      moduleId,
      component,
    },
  });

  const style: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition: transition || 'transform 250ms cubic-bezier(0.25, 1, 0.5, 1)',
    zIndex: isDragging ? 10 : undefined,
  };

  const IconComponent = getIconForComponent(component);

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-component-id={component.id}
      className={cn(
        'group flex items-center gap-2 p-3 rounded-lg border transition-all',
        isSelected
          ? 'border-border-brand bg-bg-brand-subtle ring-2 ring-border-brand/20'
          : 'border-border-secondary bg-bg-primary hover:border-border-tertiary',
        isDragging && 'opacity-50 shadow-lg',
        isNewlyAdded &&
          'animate-pulse ring-2 ring-green-500/50 border-green-500 bg-green-50'
      )}
      onClick={onSelect}
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="p-1 rounded hover:bg-bg-tertiary cursor-grab active:cursor-grabbing"
      >
        <GripVertical className="w-4 h-4 text-text-quaternary" />
      </button>

      <div className="flex items-center justify-center w-8 h-8 rounded-md bg-bg-tertiary relative">
        <IconComponent className="w-4 h-4 text-text-secondary" />
        {component.visibility && (
          <div
            className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-bg-warning flex items-center justify-center"
            title="Has visibility condition"
          >
            <Icons.Eye className="w-2 h-2 text-text-warning" />
          </div>
        )}
        {component.bindings?.sourceField && (
          <div
            className={`absolute -bottom-1 -right-1 w-3 h-3 rounded-full flex items-center justify-center ${
              component.bindings.disableOnBind
                ? 'bg-bg-error-subtle'
                : 'bg-bg-info'
            }`}
            title={
              component.bindings.disableOnBind
                ? 'Bound field (read-only)'
                : 'Has field binding'
            }
          >
            {component.bindings.disableOnBind ? (
              <Icons.Lock01 className="w-2 h-2 text-text-error" />
            ) : (
              <Icons.Link01 className="w-2 h-2 text-text-info" />
            )}
          </div>
        )}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1">
          <p className="txt-small-medium text-text-primary truncate">
            {component.component === 'NestedForm' ? (
              <NestedFormTitle
                keyId={
                  (component.props as { keyId?: string } | undefined)?.keyId
                }
                fallback={component.label || 'Nested Form'}
              />
            ) : component.component === 'KeyPersons' ? (
              <>
                {component.label || 'Key Persons'}
                {(component.props as { keyId?: string } | undefined)?.keyId && (
                  <span className="txt-xs-regular text-text-tertiary ml-1">
                    →{' '}
                    <NestedFormTitle
                      keyId={(component.props as { keyId?: string }).keyId}
                      fallback="linked form"
                    />
                  </span>
                )}
              </>
            ) : (
              component.label || component.component
            )}
          </p>
          {component.validation?.rules?.required && (
            <span className="text-text-error txt-xs-medium">*</span>
          )}
          {isCommendaOSComponentInstance(component) && (
            <Badge variant="gray" size="sm">
              Commenda OS
            </Badge>
          )}
        </div>
      </div>

      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDuplicate();
          }}
          className="p-1 rounded hover:bg-bg-secondary transition-colors"
          title="Duplicate field"
        >
          <Copy className="w-4 h-4 text-text-secondary" />
        </button>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className="p-1 rounded hover:bg-bg-error-subtle transition-colors"
          title="Remove field"
        >
          <Trash2 className="w-4 h-4 text-text-error" />
        </button>
      </div>
    </div>
  );
}

interface DroppableModuleProps {
  module: FormModule;
  stepId: string;
  selection: Selection;
  formBuilder: FormBuilderHook;
  newlyAddedComponentId?: string | null;
  isNewlyAdded?: boolean;
}

function DroppableModule({
  module,
  stepId,
  selection,
  formBuilder,
  newlyAddedComponentId,
  isNewlyAdded,
}: DroppableModuleProps) {
  const {
    attributes,
    listeners,
    setNodeRef: setSortableRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: module.id,
    animateLayoutChanges,
    data: {
      type: 'module',
      stepId,
      moduleId: module.id,
      module,
    },
  });

  const isNestedFormLocked =
    module.components.length === 1 &&
    isModuleLockingComponent(module.components[0].component);

  const { setNodeRef: setDroppableRef, isOver } = useDroppable({
    id: `module-${module.id}`,
    disabled: isNestedFormLocked,
    data: {
      type: 'module',
      stepId,
      moduleId: module.id,
    },
  });

  // Combine both refs
  const setNodeRef = (node: HTMLDivElement | null) => {
    setSortableRef(node);
    setDroppableRef(node);
  };

  const style: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition: transition || 'transform 250ms cubic-bezier(0.25, 1, 0.5, 1)',
    zIndex: isDragging ? 10 : undefined,
  };

  const isModuleSelected =
    !isNestedFormLocked &&
    selection.type === 'module' &&
    selection.stepId === stepId &&
    selection.moduleId === module.id;

  // Sort components by order first - this ensures SortableContext items match render order
  const sortedComponents = [...module.components].sort(
    (a, b) => a.order - b.order
  );
  const componentIds = sortedComponents.map((c) => c.id);

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-module-id={module.id}
      className={cn(
        'rounded-xl border-2 transition-all',
        isOver
          ? 'border-border-brand border-dashed bg-bg-brand-subtle/50'
          : isModuleSelected
            ? 'border-border-brand bg-bg-brand-subtle ring-2 ring-border-brand/20'
            : 'border-border-secondary bg-bg-primary',
        isNewlyAdded &&
          'animate-pulse ring-2 ring-green-500/50 border-green-500 bg-green-50',
        isDragging && 'opacity-50 shadow-lg'
      )}
    >
      {/* Module header - when locked (NestedForm only), clicking selects the component, not the section */}
      <div
        className={cn(
          'flex items-center justify-between p-4 border-b cursor-pointer',
          isModuleSelected
            ? 'border-border-brand/20'
            : 'border-border-secondary'
        )}
        onClick={() => {
          if (isNestedFormLocked && module.components[0]) {
            formBuilder.setSelection({
              type: 'component',
              stepId,
              moduleId: module.id,
              componentId: module.components[0].id,
            });
          } else {
            formBuilder.setSelection({
              type: 'module',
              stepId,
              moduleId: module.id,
            });
          }
        }}
      >
        <div className="flex items-center gap-2">
          <button
            type="button"
            {...attributes}
            {...listeners}
            className="p-1 rounded hover:bg-bg-tertiary cursor-grab active:cursor-grabbing"
            onClick={(e) => e.stopPropagation()}
          >
            <GripVertical className="w-4 h-4 text-text-quaternary" />
          </button>
          <span className="txt-medium-medium text-text-primary">
            {module.name}
          </span>
          {module.repeatable?.enabled && (
            <Badge variant="gray" className="ml-2">
              Repeatable ({module.repeatable.min}-{module.repeatable.max})
            </Badge>
          )}
          {module.components.length === 1 &&
            isModuleLockingComponent(module.components[0].component) && (
              <span
                title="This section is dedicated to this component and cannot have other components"
                className="flex items-center gap-1 ml-1"
              >
                <Icons.Lock01 className="w-3.5 h-3.5 text-text-tertiary" />
              </span>
            )}
          {module.description && (
            <span className="txt-small-regular text-text-tertiary">
              - {module.description}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="txt-xs-regular text-text-tertiary">
            {module.components.length} component
            {module.components.length !== 1 ? 's' : ''}
          </span>
          <Button
            type="button"
            variant="tertiary"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              formBuilder.removeModule(stepId, module.id);
            }}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Components */}
      <div className="p-4 space-y-2 min-h-[100px]">
        {componentIds.length > 0 && (
          <SortableContext
            items={componentIds}
            strategy={verticalListSortingStrategy}
          >
            {sortedComponents.map((component) => (
              <SortableComponent
                key={component.id}
                component={component}
                stepId={stepId}
                moduleId={module.id}
                isSelected={
                  selection.type === 'component' &&
                  selection.componentId === component.id
                }
                isNewlyAdded={newlyAddedComponentId === component.id}
                onSelect={() =>
                  formBuilder.setSelection({
                    type: 'component',
                    stepId,
                    moduleId: module.id,
                    componentId: component.id,
                  })
                }
                onDuplicate={() => {
                  const newComponentId = formBuilder.duplicateComponent(
                    stepId,
                    module.id,
                    component.id
                  );
                  if (newComponentId) {
                    formBuilder.setSelection({
                      type: 'component',
                      stepId,
                      moduleId: module.id,
                      componentId: newComponentId,
                    });
                  }
                }}
                onRemove={() =>
                  formBuilder.removeComponent(stepId, module.id, component.id)
                }
              />
            ))}
          </SortableContext>
        )}

        {/* {isNestedFormLocked && (
          <div className="flex items-center gap-2 py-3 px-3 rounded-lg bg-bg-tertiary">
            <Icons.Lock01 className="w-4 h-4 text-text-quaternary shrink-0" />
            <p className="txt-xs-regular text-text-tertiary">
              This section is locked to the Nested Form component
            </p>
          </div>
        )} */}

        {!isNestedFormLocked && module.components.length === 0 && (
          <div
            className={cn(
              'flex flex-col items-center justify-center py-8 rounded-lg border-2 border-dashed transition-colors',
              isOver
                ? 'border-border-brand bg-bg-brand-subtle/30'
                : 'border-border-secondary'
            )}
          >
            <Icons.Plus className="w-8 h-8 text-text-quaternary mb-2" />
            <p className="txt-small-regular text-text-tertiary">
              Drag components here
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

interface StepCardProps {
  step: FormStep;
  isActive: boolean;
  selection: Selection;
  formBuilder: FormBuilderHook;
  newlyAddedComponentId?: string | null;
  newlyAddedModuleId?: string | null;
}

function StepCard({
  step,
  isActive,
  selection,
  formBuilder,
  newlyAddedComponentId,
  newlyAddedModuleId,
}: StepCardProps) {
  const isStepSelected =
    selection.type === 'step' && selection.stepId === step.id;

  return (
    <div
      data-step-id={step.id}
      className={cn(
        'rounded-2xl border-2 transition-all bg-bg-primary',
        isActive
          ? 'border-border-brand shadow-md'
          : isStepSelected
            ? 'border-border-brand'
            : 'border-border-secondary',
        isStepSelected && 'ring-2 ring-border-brand/20'
      )}
    >
      {/* Step header */}
      <div
        className={cn(
          'flex items-center justify-between p-4 cursor-pointer rounded-t-2xl',
          isActive
            ? 'bg-bg-brand-subtle'
            : isStepSelected
              ? 'bg-bg-brand-subtle'
              : 'bg-bg-secondary'
        )}
        onClick={() =>
          formBuilder.setSelection({
            type: 'step',
            stepId: step.id,
          })
        }
      >
        <div className="flex items-center gap-3">
          <div
            className={cn(
              'flex items-center justify-center w-8 h-8 rounded-full txt-small-medium',
              isActive
                ? 'bg-bg-brand text-text-inverse'
                : 'bg-bg-tertiary text-text-secondary'
            )}
          >
            {step.order}
          </div>
          <div>
            <h3 className="txt-medium-medium text-text-primary">
              {step.title}
            </h3>
            {step.estimatedMinutes && (
              <div className="flex items-center gap-1 txt-xs-regular text-text-tertiary">
                <Clock className="w-3 h-3" />
                {step.estimatedMinutes} min
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="tertiary"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              formBuilder.addModule(step.id);
            }}
          >
            <Plus className="w-4 h-4 mr-1" />
            Add Section
          </Button>
          <Button
            type="button"
            variant="tertiary"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              formBuilder.removeStep(step.id);
            }}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Modules */}
      <div className="p-4 space-y-4 bg-bg-secondary rounded-b-2xl">
        {step.modules.length > 0 && (
          <SortableContext
            items={step.modules
              .sort((a, b) => a.order - b.order)
              .map((m) => m.id)}
            strategy={verticalListSortingStrategy}
          >
            {step.modules
              .sort((a, b) => a.order - b.order)
              .map((module) => (
                <DroppableModule
                  key={module.id}
                  module={module}
                  stepId={step.id}
                  selection={selection}
                  formBuilder={formBuilder}
                  newlyAddedComponentId={newlyAddedComponentId}
                  isNewlyAdded={newlyAddedModuleId === module.id}
                />
              ))}
          </SortableContext>
        )}

        {step.modules.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 rounded-xl border-2 border-dashed border-border-secondary">
            <Icons.Plus className="w-10 h-10 text-text-quaternary mb-3" />
            <p className="txt-medium-regular text-text-tertiary mb-2">
              No sections yet
            </p>
            <Button
              type="button"
              variant="secondary"
              onClick={() => formBuilder.addModule(step.id)}
            >
              <Plus className="w-4 h-4 mr-1" />
              Add Section
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

interface SortableStepTabProps {
  step: FormStep;
  index: number;
  isActive: boolean;
  onClick: () => void;
}

function SortableStepTab({
  step,
  index,
  isActive,
  onClick,
}: SortableStepTabProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: step.id,
    animateLayoutChanges,
    data: {
      type: 'step',
      step,
      index,
    },
  });

  const style: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition: transition || 'transform 250ms cubic-bezier(0.25, 1, 0.5, 1)',
    zIndex: isDragging ? 10 : undefined,
  };

  return (
    <button
      type="button"
      ref={setNodeRef}
      style={style}
      onClick={onClick}
      className={cn(
        'flex items-center gap-2 px-4 py-2 rounded-lg txt-small-medium transition-colors whitespace-nowrap',
        isActive
          ? 'bg-bg-brand text-text-inverse'
          : 'bg-bg-secondary text-text-secondary hover:bg-bg-tertiary',
        isDragging && 'opacity-50 shadow-lg z-50'
      )}
      {...attributes}
      {...listeners}
    >
      <GripVertical className="w-3 h-3 opacity-50" />
      <span className="flex items-center justify-center w-5 h-5 rounded-full bg-current/20 txt-xs-medium">
        {step.order}
      </span>
      {step.title}
    </button>
  );
}

interface FormCanvasProps {
  ref: React.RefObject<HTMLDivElement>;
  formBuilder: FormBuilderHook;
  activeStepIndex: number;
  onStepChange: (index: number) => void;
  newlyAddedComponentId?: string | null;
  newlyAddedModuleId?: string | null;
  onAddStepClick?: () => void;
}

export const FormCanvas = React.forwardRef<HTMLDivElement, FormCanvasProps>(
  (
    {
      formBuilder,
      activeStepIndex,
      onStepChange,
      newlyAddedComponentId,
      newlyAddedModuleId,
      onAddStepClick,
    },
    ref
  ) => {
    const { dsl, selection } = formBuilder;

    return (
      <div className="h-full overflow-hidden flex flex-col bg-bg-primary">
        {/* Form header */}
        <div
          ref={ref}
          className="bg-bg-primary border-b border-border-secondary p-2.5 px-4"
        >
          <div className="flex items-center justify-between">
            <div className="flex flex-row items-center gap-2">
              <h1 className="txt-large-medium text-text-primary">
                {dsl?.form?.title}
              </h1>
              <p className="text-xs mt-[2px] text-text-tertiary">
                • {dsl?.steps?.length} step
                {dsl?.steps?.length !== 1 ? 's' : ''}{' '}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() =>
                  formBuilder.setSelection({
                    type: null,
                  })
                }
              >
                <Icons.Settings01 className="w-4 h-4 mr-1" />
                Form Settings
              </Button>
            </div>
          </div>
        </div>

        {/* Step tabs - only show for multi-step forms */}
        {dsl?.form?.type !== 'single-page' && (
          <div className="bg-bg-primary border-b border-border-secondary px-4 py-2 flex items-center gap-2 overflow-x-auto flex-wrap">
            {dsl?.steps?.length > 0 && (
              <SortableContext
                items={dsl?.steps?.map((s) => s.id)}
                strategy={horizontalListSortingStrategy}
              >
                {dsl?.steps?.map((step, index) => (
                  <SortableStepTab
                    key={step.id}
                    step={step}
                    index={index}
                    isActive={activeStepIndex === index}
                    onClick={() => onStepChange(index)}
                  />
                ))}
              </SortableContext>
            )}
            <Button
              variant="tertiary"
              size="sm"
              onClick={onAddStepClick}
              className="whitespace-nowrap"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add Step
            </Button>
          </div>
        )}

        {/* Canvas content */}
        <div className="flex-1 overflow-y-auto p-6 bg-bg-tertiary">
          <div className="max-w-4xl mx-auto">
            {dsl?.steps?.[activeStepIndex] && (
              <StepCard
                step={dsl?.steps?.[activeStepIndex]}
                isActive
                selection={selection}
                formBuilder={formBuilder}
                newlyAddedComponentId={newlyAddedComponentId}
                newlyAddedModuleId={newlyAddedModuleId}
              />
            )}

            {dsl?.steps?.length === 0 && (
              <div className="flex flex-col items-center justify-center py-20 rounded-2xl border-2 border-dashed border-border-secondary bg-bg-primary">
                <Icons.File02 className="w-16 h-16 text-text-quaternary mb-4" />
                <h3 className="txt-large-medium text-text-primary mb-2">
                  No steps yet
                </h3>
                <p className="txt-medium-regular text-text-tertiary mb-4">
                  Add your first step to start building the form
                </p>
                <Button variant="primary" onClick={() => formBuilder.addStep()}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add First Step
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
);

FormCanvas.displayName = 'FormCanvas';

export default FormCanvas;
